import React from 'react'

interface BreadcrumbItem {
  id: string
  name: string
  path?: string
}

interface FileExplorerDragOverlayProps {
  isDragOver: boolean
  selectedFilesCount: number
  isDraggingFile: boolean
  clipboardFileIds: string[]
  onClearClipboard: () => void
  parentId?: string
  breadcrumbItems?: BreadcrumbItem[]
}

const FileExplorerDragOverlay: React.FC<FileExplorerDragOverlayProps> = ({
  isDragOver,
  selectedFilesCount,
  isDraggingFile,
  clipboardFileIds,
  onClearClipboard,
  parentId,
  breadcrumbItems,
}) => {
  // Generate breadcrumb path for display
  const getBreadcrumbPath = (): string => {
    if (!breadcrumbItems || breadcrumbItems.length === 0) {
      return 'My Drive'
    }

    const path = breadcrumbItems.map(item => item.name).join(' › ')

    return `Home › ${path}`
  }

  return (
    <>
      {/* Floating drag indicator */}
      {selectedFilesCount > 0 && isDraggingFile && (
        <div className="fixed bottom-6 right-6 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M7 16l-4-4m0 0l4-4m-4 4h18"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          <span className="font-medium">
            Moving {selectedFilesCount} item{selectedFilesCount === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {/* Clipboard indicator */}
      {clipboardFileIds.length > 0 && (
        <div className="fixed bottom-6 left-6 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          <span className="font-medium">
            {clipboardFileIds.length} file{clipboardFileIds.length === 1 ? '' : 's'} in clipboard
          </span>
          <button
            className="ml-2 hover:bg-white/20 rounded p-1 transition-colors"
            onClick={onClearClipboard}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                d="M6 18L18 6M6 6l12 12"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </button>
        </div>
      )}

      {/* Bottom message area for root drag */}
      {isDragOver && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-4 bg-blue-500/10 border border-blue-400/30 rounded-xl backdrop-blur-sm shadow-lg max-w-md w-auto">
          <div className="flex items-center justify-center gap-3">
            <svg
              className="w-6 h-6 text-blue-500 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            <p className="text-blue-500 dark:text-blue-400 font-medium">
              {parentId
                ? `Your files will be uploaded to ${getBreadcrumbPath()}.`
                : 'Your files will be uploaded to My Drive.'}
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default FileExplorerDragOverlay
